import { useCallback, useMemo, useState } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import {
  getConfigurableKanbanFields,
  getKanbanCardFieldCatalog,
  getKanbanCardFieldDefaults,
} from "@/modern/constants/kanbanFieldUtils";
import { MAX_KANBAN_VISIBLE_FIELDS } from "@/modern/constants/kanbanCardFieldCatalog";

type EntityTypeValue = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

type KanbanCardFieldsPreference = Partial<Record<EntityTypeValue, string[]>>;

const parseKanbanCardFieldsPreference = (raw: string | null | undefined): KanbanCardFieldsPreference => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed as KanbanCardFieldsPreference;
  } catch {
    return {};
  }
};

const serializeKanbanCardFieldsPreference = (pref: KanbanCardFieldsPreference): string => JSON.stringify(pref);

const filterValidVisibleFieldIds = (entityType: string, fieldIds: string[] | undefined | null): string[] => {
  const catalog = getKanbanCardFieldCatalog(entityType);
  const configurable = getConfigurableKanbanFields(catalog);
  const validIds = new Set(configurable.map((f) => f.id));
  const source = fieldIds ?? [];
  const seen = new Set<string>();
  const result: string[] = [];

  for (const id of source) {
    if (!validIds.has(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    if (result.length >= MAX_KANBAN_VISIBLE_FIELDS) break;
  }

  return result;
};

const resolveVisibleFieldIds = (
  entityType: string,
  allPreferences: KanbanCardFieldsPreference,
  entityTypeKey: EntityTypeValue
): string[] => {
  if (!Object.prototype.hasOwnProperty.call(allPreferences, entityTypeKey)) {
    return getKanbanCardFieldDefaults(entityType);
  }
  return filterValidVisibleFieldIds(entityType, allPreferences[entityTypeKey]);
};

export const useKanbanCardFields = (entityType: string) => {
  const { userPreferences } = useSwivuBryntumContext();
  const { version } = useSwivuStore({
    storeId: STORE_KEYS.userPreferencesStore,
    subscriberFn: ({ record: changedRecord }: { record?: { key?: string } }) =>
      changedRecord?.key === PERSISTENCE_STATE_KEYS.kanbanCardFields,
  });
  const [revision, setRevision] = useState(0);

  const catalog = useMemo(() => getKanbanCardFieldCatalog(entityType), [entityType]);
  const defaults = useMemo(() => getKanbanCardFieldDefaults(entityType), [entityType]);

  const allPreferences = useMemo(() => {
    if (!userPreferences) return {};
    const raw = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.kanbanCardFields, "{}", false);
    return parseKanbanCardFieldsPreference(raw);
  }, [userPreferences, version, revision]);

  const visibleFieldIds = useMemo(
    () => resolveVisibleFieldIds(entityType, allPreferences, entityType as EntityTypeValue),
    [entityType, allPreferences]
  );

  const saveVisibleFieldIds = useCallback(
    (nextIds: string[]) => {
      if (!userPreferences) return;
      const sanitized = filterValidVisibleFieldIds(entityType, nextIds);
      const nextPref: KanbanCardFieldsPreference = {
        ...allPreferences,
        [entityType]: sanitized,
      };
      userPreferences.updateSetting(
        PERSISTENCE_STATE_KEYS.kanbanCardFields,
        serializeKanbanCardFieldsPreference(nextPref),
        false
      );
      setRevision((r) => r + 1);
    },
    [allPreferences, entityType, userPreferences]
  );

  return {
    catalog,
    visibleFieldIds,
    defaults,
    maxFields: MAX_KANBAN_VISIBLE_FIELDS,
    saveVisibleFieldIds,
  };
};
